'use client';

import * as React from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import NextImage from 'next/image';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import CmsImage from '@/components/ui/CmsImage';
import SwapTextButton from '@/components/ui/SwapTextButton/SwapTextButton';
import { getCmsButtonLinkProps } from '@/lib/cms-button-target';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';
import { MEDIA_PLACEHOLDER } from '@/lib/media-placeholder';

import { MapPin, Phone, Loader2, X } from 'lucide-react';
import type { CommonSpacerZone } from '@/types/homepage';
import {
  DealerLocatorType,
  DealerFormComponent,
  MediaSliderComponent,
  FaqWithFormComponent,
  DealerSearchResult,
} from '@/types/dealer';
import { wclApiService, StateItem, DistrictItem } from '@/services/wcl-api.service';
import { searchDealers } from '@/services/dealer-search.service';
import { createDealerSlug } from '@/services/dealer-detail.service';
import { useDealerStore } from '@/store/dealer.store';
import { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import FaqEnquirySection from '@/components/sections/Products/FaqEnquirySection';
import CommonSpacer from '@/components/sections/shared/CommonSpacer';
import { useSalesCallback } from '@/components/sales-callback/SalesCallbackProvider';
import type { LeadForm } from '@/types/lead-forms';
import { useRecaptcha } from '@/hooks/useRecaptcha';
import { useGeoDetect } from '@/hooks/useGeoDetect';
import { usePrefillSavedLocation } from '@/hooks/useSavedLocation';
import { clearGeoCache } from '@/lib/geo/storage';
import { resolveStrapiMediaUrl } from '@/lib/strapi-media-url';
import { isPlaceholderNavHref } from '@/lib/header-nav';
import { localizeUrl } from '@/lib/homepage-mappers';
import dynamic from 'next/dynamic';
import CmsRichTextHeading from '@/components/ui/CmsRichTextHeading';

// Lazy-load the Google Map component so its ~250KB SDK + tile fetches don't
// fire on initial page load (was tanking mobile FCP to 8s and LCP to 13s).
// Falls back to a no-op while the chunk loads; the JPG placeholder still
// renders behind it via `showMap === false` so users never see a blank slot.
const GoogleMap = dynamic(() => import('@/components/ui/GoogleMap/GoogleMap'), {
  ssr: false,
});

const DEALER_RESULTS_BATCH_SIZE = 8;

function filterDealersByName(dealers: DealerSearchResult[], searchTerm: string) {
  const normalizedSearchTerm = searchTerm.trim().toLocaleLowerCase();
  if (!normalizedSearchTerm) return dealers;

  return dealers.filter((dealer) =>
    (dealer.dealerName || dealer.name || '').toLocaleLowerCase().includes(normalizedSearchTerm),
  );
}

function extractYouTubeIdFromIframe(iframeHtml: string | null | undefined): string | null {
  if (!iframeHtml) return null;
  const match = iframeHtml.match(/youtube(?:-nocookie)?\.com\/embed\/([a-z0-9_-]{6,})/i);
  return match?.[1] ?? null;
}

/** YouTube thumbnail for a CMS iframe embed snippet. */
function youtubeThumbnailFromIframe(iframeHtml: string | null | undefined): string | null {
  const id = extractYouTubeIdFromIframe(iframeHtml);
  return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : null;
}

function youtubeEmbedSrc(videoId: string, autoplay: boolean): string {
  const params = new URLSearchParams({
    rel: '0',
    modestbranding: '1',
    playsinline: '1',
  });
  if (autoplay) params.set('autoplay', '1');
  return `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
}

function YouTubePlayButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="group pointer-events-auto flex flex-col items-center gap-2 transition-transform hover:scale-105"
    >
      <span className="flex h-12 w-[4.25rem] items-center justify-center rounded-xl bg-[#FF0000] shadow-lg sm:h-14 sm:w-[4.75rem]">
        <svg className="ml-1 h-7 w-7 fill-white sm:h-8 sm:w-8" viewBox="0 0 24 24" aria-hidden>
          <path d="M8 5v14l11-7z" />
        </svg>
      </span>
    </button>
  );
}

type GeoDetectState = ReturnType<typeof useGeoDetect>;

/** Geo-detect status banner: working spinner, detected-location summary, or
 *  the "Use my location" call-to-action. */
function GeoDetectBanner({
  geo,
  onChangeLocation,
}: {
  geo: GeoDetectState;
  onChangeLocation: () => void;
}) {
  if (geo.isWorking) {
    return (
      <div className="mt-4 flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600 sm:text-sm">
        <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-[#ed1c22]" />
        <span>
          {geo.status === 'requesting-permission' && 'Waiting for location permission…'}
          {geo.status === 'geocoding' && 'Looking up your area…'}
          {geo.status === 'matching' && 'Finding dealers nearby…'}
          {geo.status === 'loading-cache' && 'Checking your saved location…'}
        </span>
      </div>
    );
  }
  if (
    (geo.status === 'matched' || geo.status === 'cache-hit' || geo.status === 'partial-match') &&
    geo.stateName
  ) {
    return (
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-md border border-[#ed1c22]/20 bg-[#ed1c22]/[0.04] px-3 py-2 text-xs sm:text-sm">
        <span className="flex items-center gap-2 text-gray-700">
          <MapPin className="h-4 w-4 shrink-0 text-[#ed1c22]" />
          <span>
            Showing dealers near{' '}
            <strong className="text-gray-900">
              {geo.districtName ? `${geo.districtName}, ` : ''}
              {geo.stateName}
            </strong>
          </span>
        </span>
        <button
          type="button"
          onClick={onChangeLocation}
          className="shrink-0 text-xs font-semibold text-[#ed1c22] underline-offset-2 hover:underline"
        >
          Change
        </button>
      </div>
    );
  }
  return (
    <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-md border border-[#ed1c22]/30 bg-[#fff7f7] px-3 py-2.5 text-xs sm:text-sm">
      <span className="flex items-center gap-2 text-gray-700">
        <MapPin className="h-4 w-4 shrink-0 text-[#ed1c22]" />
        <span>
          {geo.errorMessage ?? 'Allow location access to find dealers near you automatically.'}
        </span>
      </span>
      <button
        type="button"
        onClick={() => geo.triggerDetect()}
        disabled={geo.status === 'unsupported'}
        className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-[#ed1c22] bg-[#ed1c22] px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-[#c8161d] disabled:cursor-not-allowed disabled:opacity-50"
      >
        <MapPin className="h-3.5 w-3.5" />
        Use my location
      </button>
    </div>
  );
}

/** Renders a single media-slider slide: an active YouTube/iframe player, or the
 *  poster image with a play button + title overlay. */
function DealerSlideMedia({
  slide,
  isPlaying,
  onPlay,
}: {
  slide: MediaSliderComponent['slide'][number];
  isPlaying: boolean;
  onPlay: () => void;
}) {
  const youtubeId = extractYouTubeIdFromIframe(slide.iframeLink);
  if (isPlaying && youtubeId) {
    return (
      <iframe
        src={youtubeEmbedSrc(youtubeId, true)}
        title={slide.slideTitle || 'Dealer story video'}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        className="absolute inset-0 h-full w-full border-0"
      />
    );
  }
  if (isPlaying && slide.iframeLink) {
    return (
      <div
        className="absolute inset-0 [&_iframe]:h-full [&_iframe]:w-full [&_iframe]:border-0"
        dangerouslySetInnerHTML={{ __html: slide.iframeLink }}
      />
    );
  }
  const playLabel = `Play ${slide.slideTitle || 'video'}`;
  if (isPlaying) return null;

  return (
    <>
      <CmsImage
        src={
          (slide.media?.url ? resolveStrapiMediaUrl(slide.media.url) : null) ||
          youtubeThumbnailFromIframe(slide.iframeLink) ||
          '/images/del_loc_img.jpg'
        }
        alt={slide.slideTitle || 'Dealer story'}
        fill
        sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
        className="object-cover"
        fallbackSrc={MEDIA_PLACEHOLDER}
      />

      <div className="absolute inset-0 bg-black/20" />

      {youtubeId ? (
        <div className="absolute inset-0 flex items-center justify-center">
          <YouTubePlayButton label={playLabel} onClick={onPlay} />
        </div>
      ) : null}

      {slide.slideTitle ? (
        <div className="pointer-events-none absolute bottom-4 left-4 right-4">
          <span className="text-sm font-medium text-white">{slide.slideTitle}</span>
        </div>
      ) : null}
    </>
  );
}

interface DealerListProps {
  locale: string;
  dealerData: DealerLocatorType | null;
  leadForms?: LeadForm[];
}

export default function DealerList({ locale, dealerData, leadForms }: DealerListProps) {
  const router = useRouter();
  const isMountedRef = useRef(false);
  const searchParams = useSearchParams();
  const initialParamsAppliedRef = useRef(false);
  // Target for the post-navigation auto-scroll when the user arrives via the
  // home-page "Search Dealer" form (?autoSearch=1).
  const searchSectionRef = useRef<HTMLDivElement | null>(null);
  const { executeRecaptcha, isLoaded: isRecaptchaLoaded } = useRecaptcha();
  const { showTab: showSalesCallbackButton } = useSalesCallback();
  const { setSelectedDealer, setDealersList, setSearchParams } = useDealerStore();

  const [mounted, setMounted] = useState(false);
  const [selectedState, setSelectedState] = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [states, setStates] = useState<StateItem[]>([]);
  const [districts, setDistricts] = useState<DistrictItem[]>([]);
  const [isLoadingStates, setIsLoadingStates] = useState(false);
  const [isLoadingDistricts, setIsLoadingDistricts] = useState(false);
  const [dealers, setDealers] = useState<DealerSearchResult[]>([]);
  const [visibleDealerCount, setVisibleDealerCount] = useState(DEALER_RESULTS_BATCH_SIZE);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  /** Location labels from the last completed search (for empty-state copy). */
  const [lastSearchLabels, setLastSearchLabels] = useState<{
    stateName: string;
    districtName: string;
  } | null>(null);
  const [stateSearchTerm, setStateSearchTerm] = useState('');
  const [districtSearchTerm, setDistrictSearchTerm] = useState('');
  const [dealerNameSearchTerm, setDealerNameSearchTerm] = useState('');
  const [showStateDropdown, setShowStateDropdown] = useState(false);
  const [showDistrictDropdown, setShowDistrictDropdown] = useState(false);
  const [filteredStates, setFilteredStates] = useState<StateItem[]>([]);
  const [filteredDistricts, setFilteredDistricts] = useState<DistrictItem[]>([]);
  const [playingSlideId, setPlayingSlideId] = useState<number | null>(null);
  const [pendingDistrictId, setPendingDistrictId] = useState<string | null>(null);
  const [shouldAutoSearch, setShouldAutoSearch] = useState(false);
  // Lazy-mount the Google Map: stays as the static JPG placeholder until
  // the user actually engages (search returns dealers, OR they scroll the
  // map container near the viewport). Keeps the Google Maps SDK off the
  // initial critical path.
  const [showMap, setShowMap] = useState(false);
  /** Map center from the first page-load geo detect (undefined until then). */
  const [geoMapCenter, setGeoMapCenter] = useState<{ lat: number; lng: number } | undefined>(
    undefined,
  );
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const dealerResultsScrollRef = useRef<HTMLDivElement | null>(null);
  const loadMoreDealersRef = useRef<HTMLDivElement | null>(null);

  // Tracks whether the geo-detect result has already been applied to the
  // dropdowns. Guards against the effect re-firing on re-renders and
  // overwriting a user who has since manually changed their selection.
  const geoAppliedRef = useRef(false);
  /** Ensures we only pan the map to GPS coords once on initial page load. */
  const geoMapSyncedRef = useRef(false);

  // Geo detect: uses the shared site-wide cache (from GlobalLocationPrompt
  // or a prior visit). Auto-prompt is off here — the global soft prompt owns
  // first-visit permission UX; the "Use my location" CTA still calls
  // triggerDetect(). Disabled when URL already has explicit state/district.
  const hasUrlParams = Boolean(searchParams?.get('state') || searchParams?.get('district'));
  const geo = useGeoDetect({ enabled: mounted && !hasUrlParams, autoPrompt: false });
  const filteredDealers = React.useMemo(
    () => filterDealersByName(dealers, dealerNameSearchTerm),
    [dealerNameSearchTerm, dealers],
  );

  useEffect(() => {
    isMountedRef.current = true;
    setMounted(true);

    // Click outside handler to close dropdowns
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.dropdown-container')) {
        setShowStateDropdown(false);
        setShowDistrictDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      isMountedRef.current = false;
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Activate the real Google Map when the user actually has dealer results
  // to plot. Avoids loading the SDK if the user just lands and bounces.
  useEffect(() => {
    if (dealers.length > 0) setShowMap(true);
  }, [dealers.length]);

  // The dealer API returns the complete result set in one response. Render it
  // in small batches so large districts stay responsive, then reveal the next
  // batch as the user approaches the end of the scrollable results panel.
  useEffect(() => {
    setVisibleDealerCount(Math.min(DEALER_RESULTS_BATCH_SIZE, filteredDealers.length));
    dealerResultsScrollRef.current?.scrollTo({ top: 0 });
  }, [filteredDealers]);

  useEffect(() => {
    const scrollContainer = dealerResultsScrollRef.current;
    const loadMoreTarget = loadMoreDealersRef.current;

    if (!scrollContainer || !loadMoreTarget || visibleDealerCount >= filteredDealers.length) return;

    if (typeof IntersectionObserver === 'undefined') {
      setVisibleDealerCount(filteredDealers.length);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;

        setVisibleDealerCount((currentCount) =>
          Math.min(currentCount + DEALER_RESULTS_BATCH_SIZE, filteredDealers.length),
        );
      },
      {
        root: scrollContainer,
        rootMargin: '0px 0px 160px 0px',
      },
    );

    observer.observe(loadMoreTarget);
    return () => observer.disconnect();
  }, [filteredDealers.length, visibleDealerCount]);

  // On first successful geo-detect (page load / cache), center the map on
  // the user's coordinates once — then leave later pans to search results.
  useEffect(() => {
    if (geoMapSyncedRef.current || hasUrlParams) return;
    if (geo.status !== 'matched' && geo.status !== 'cache-hit' && geo.status !== 'partial-match') {
      return;
    }
    if (geo.lat == null || geo.lng == null) return;

    geoMapSyncedRef.current = true;
    setGeoMapCenter({ lat: geo.lat, lng: geo.lng });
    setShowMap(true);
  }, [geo.status, geo.lat, geo.lng, hasUrlParams]);

  // Secondary activation: if the user scrolls the map area into view (or
  // close to it), activate the map. This handles the case where someone
  // arrives via a deep link or scrolls down before searching. Once active,
  // the observer disconnects — we never unmount the map.
  useEffect(() => {
    if (showMap) return;
    const target = mapContainerRef.current;
    if (!target || typeof IntersectionObserver === 'undefined') return;

    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShowMap(true);
          obs.disconnect();
        }
      },
      { rootMargin: '200px' },
    );
    obs.observe(target);
    return () => obs.disconnect();
  }, [showMap]);

  // Load initial states
  useEffect(() => {
    if (!mounted) return;

    const loadStates = async () => {
      setIsLoadingStates(true);
      try {
        const statesData = await wclApiService.getStates();

        if (isMountedRef.current) {
          setStates(statesData);
        }
      } catch (error) {
        console.error('❌ Error loading states:', error);
        if (isMountedRef.current) {
          toast.error('Failed to load states. Please try again.');
        }
      } finally {
        if (isMountedRef.current) {
          setIsLoadingStates(false);
        }
      }
    };

    loadStates();
  }, [mounted]);

  // Load districts when state changes
  useEffect(() => {
    if (!mounted || !selectedState) {
      setDistricts([]);
      setSelectedDistrict('');
      return;
    }

    let cancelled = false;

    const loadDistricts = async () => {
      setIsLoadingDistricts(true);
      try {
        const districtsData = await wclApiService.getDistricts('', selectedState);

        if (isMountedRef.current && !cancelled) {
          setDistricts(districtsData);
          // Do not clear selectedDistrict here — handleStateChange already
          // resets it when the user changes state. Clearing on every response
          // races with user selection / completed searches (esp. Strict Mode
          // double-fetch) and hides the post-search empty state.
        }
      } catch (error) {
        console.error('❌ Error loading districts:', error);
        if (isMountedRef.current && !cancelled) {
          toast.error('Failed to load districts. Please try again.');
          setDistricts([]); // Clear districts on error
        }
      } finally {
        if (isMountedRef.current && !cancelled) {
          setIsLoadingDistricts(false);
        }
      }
    };

    loadDistricts();

    return () => {
      cancelled = true;
    };
  }, [selectedState, mounted]);

  // Filter states — while the dropdown is open show the full list until the user types
  // (avoids only showing the currently selected state after reopening).
  useEffect(() => {
    if (!showStateDropdown || !stateSearchTerm.trim()) {
      setFilteredStates(states);
      return;
    }
    const selectedName = states.find((s) => s.id === selectedState)?.name;
    if (selectedName && stateSearchTerm === selectedName) {
      setFilteredStates(states);
      return;
    }
    setFilteredStates(
      states.filter((state) => state.name.toLowerCase().includes(stateSearchTerm.toLowerCase())),
    );
  }, [states, stateSearchTerm, showStateDropdown, selectedState]);

  // Filter districts — same reopen behaviour as states.
  useEffect(() => {
    if (!showDistrictDropdown || !districtSearchTerm.trim()) {
      setFilteredDistricts(districts);
      return;
    }
    const selectedName = districts.find((d) => d.id === selectedDistrict)?.name;
    if (selectedName && districtSearchTerm === selectedName) {
      setFilteredDistricts(districts);
      return;
    }
    setFilteredDistricts(
      districts.filter((district) =>
        district.name.toLowerCase().includes(districtSearchTerm.toLowerCase()),
      ),
    );
  }, [districts, districtSearchTerm, showDistrictDropdown, selectedDistrict]);

  // Hydrate from URL query params (?state=&district=&autoSearch=1) once on mount.
  // District is deferred via pendingDistrictId because the districts list must
  // load for the chosen state before we can apply the selection — and the
  // load-districts effect explicitly clears any pre-existing district value.
  useEffect(() => {
    if (!mounted || initialParamsAppliedRef.current) return;
    const stateParam = searchParams?.get('state');
    const districtParam = searchParams?.get('district');
    const auto = searchParams?.get('autoSearch') === '1';
    if (stateParam) setSelectedState(stateParam);
    if (districtParam) setPendingDistrictId(districtParam);
    if (auto) {
      setShouldAutoSearch(true);
      // Scroll to the search section so the user lands on the form (and the
      // results just below it) instead of the hero. Defer to the next frame
      // so the ref has attached after the first paint.
      requestAnimationFrame(() => {
        searchSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
    initialParamsAppliedRef.current = true;
  }, [mounted, searchParams]);

  // Once states load, sync the search-input display label for a URL-prefilled state.
  useEffect(() => {
    if (selectedState && states.length > 0 && !stateSearchTerm) {
      const match = states.find((s) => s.id === selectedState);
      if (match) setStateSearchTerm(match.name);
    }
  }, [states, selectedState, stateSearchTerm]);

  // Once districts load, apply the pending district from URL (if any).
  useEffect(() => {
    if (!pendingDistrictId || districts.length === 0) return;
    const match = districts.find((d) => d.id === pendingDistrictId);
    if (match) {
      setSelectedDistrict(pendingDistrictId);
      setDistrictSearchTerm(match.name);
    }
    setPendingDistrictId(null);
  }, [districts, pendingDistrictId]);

  // Auto-fire dealer search once both state and district are populated from URL.
  useEffect(() => {
    if (
      shouldAutoSearch &&
      selectedState &&
      selectedDistrict &&
      !pendingDistrictId &&
      !isSearching
    ) {
      setShouldAutoSearch(false);
      handleSearch();
    }
    // handleSearch is intentionally omitted — it would change identity every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldAutoSearch, selectedState, selectedDistrict, pendingDistrictId, isSearching]);

  // Apply geo-detect result to the dropdowns + trigger auto-search. We
  // piggyback on the existing URL-param machinery (setSelectedState
  // triggers district load; setPendingDistrictId waits for districts to
  // arrive; setShouldAutoSearch fires the search once both selected).
  //
  // Skipped when:
  //   - URL params present (deep-link wins over geo)
  //   - geoAppliedRef already true (don't clobber user's manual changes
  //     if the geo result resolves late)
  //   - geo only matched the state (partial) — we still pre-fill the
  //     state but DON'T auto-fire, so the user can pick their district
  //     deliberately rather than searching for the wrong one
  useEffect(() => {
    if (!mounted || hasUrlParams || geoAppliedRef.current) return;
    if (geo.status !== 'matched' && geo.status !== 'cache-hit' && geo.status !== 'partial-match') {
      return;
    }
    if (!geo.stateId) return;

    geoAppliedRef.current = true;
    setSelectedState(geo.stateId);
    if (geo.stateName) setStateSearchTerm(geo.stateName);

    if (geo.districtId) {
      setPendingDistrictId(geo.districtId);
      // Only auto-search when BOTH state and district matched. Partial
      // matches leave it to the user — they may want to pick a nearby
      // district themselves.
      setShouldAutoSearch(true);
    }
  }, [mounted, hasUrlParams, geo.status, geo.stateId, geo.stateName, geo.districtId]);

  // Global location prompt writes the shared geo cache on another hook
  // instance — pick it up here so dropdowns fill even when this page's
  // useGeoDetect already finished in `idle` before the cache existed.
  usePrefillSavedLocation((loc) => {
    if (hasUrlParams || !loc.stateId) return;
    if (!geoAppliedRef.current) {
      geoAppliedRef.current = true;
      setSelectedState(loc.stateId);
      if (loc.stateName) setStateSearchTerm(loc.stateName);
    } else {
      // Cache may enrich later with district after a state-only match.
      setSelectedState((prev) => prev || loc.stateId!);
      if (loc.stateName) setStateSearchTerm((prev) => prev || loc.stateName!);
    }
    if (loc.districtId) {
      setPendingDistrictId((prev) => prev ?? loc.districtId);
      setShouldAutoSearch(true);
    }
  }, mounted && !hasUrlParams);

  // Get dealer zone components from API data (rendered in CMS order).
  const dealerZones = dealerData?.dealerZone ?? [];
  const dealerFormComponent = dealerZones.find(
    (comp): comp is DealerFormComponent => comp.__component === 'unique-forms.dealer-form',
  );
  const mediaSliderComponent = dealerZones.find(
    (comp): comp is MediaSliderComponent => comp.__component === 'modules.media-slider',
  );
  const faqComponent = dealerZones.find(
    (comp): comp is FaqWithFormComponent => comp.__component === 'modules.faqwith-form',
  );

  const renderSpacersByType = (type: CommonSpacerZone['__component']) =>
    dealerZones
      .filter((zone): zone is CommonSpacerZone => zone.__component === type)
      .map((zone) => <CommonSpacer key={`${zone.__component}-${zone.id}`} zone={zone} />);

  // Helper functions for dynamic content with fallbacks
  const getHeroContent = () => {
    if (dealerData) {
      return {
        title: dealerData.pageTitle,
        titleRichText: dealerData.titleRichText ?? null,
        subtitle: dealerData.pageSubheading?.replace(/<[^>]*>/g, '') || '', // Strip HTML tags
      };
    }
    return {
      title: 'Find a Dealer Near You',
      titleRichText: null,
      subtitle:
        'With a massive network of 5,000+ dealers, there is always a trusted partner near you to ensure your construction gets the foundation it deserves.',
    };
  };

  const getBackgroundImage = (): string => {
    if (dealerData?.bannerMedia?.url) {
      const resolved = resolveStrapiMediaUrl(dealerData.bannerMedia.url);
      if (resolved) return resolved;
    }
    return '/images/del_loc_img.jpg'; // Fallback image
  };

  const handleStateChange = (stateId: string) => {
    const selectedStateObj = states.find((s) => s.id === stateId);

    // Clear everything when state changes
    setSelectedState(stateId);
    setSelectedDistrict('');
    setStateSearchTerm(selectedStateObj?.name || '');
    setDistrictSearchTerm(''); // Clear district search
    setDealerNameSearchTerm('');
    setShowStateDropdown(false);
    setShowDistrictDropdown(false);
    setIsSearching(false);
    setHasSearched(false);
    setLastSearchLabels(null);
    setDealers([]); // Clear previous dealer results
    setDistricts([]); // Clear previous districts to force reload
    setFilteredDistricts([]); // Clear filtered districts
  };

  const handleDistrictChange = (districtId: string) => {
    const selectedDistrictObj = districts.find((d) => d.id === districtId);

    setSelectedDistrict(districtId);
    setDistrictSearchTerm(selectedDistrictObj?.name || '');
    setDealerNameSearchTerm('');
    setShowDistrictDropdown(false);
    setIsSearching(false);
    setHasSearched(false);
    setLastSearchLabels(null);
    setDealers([]); // Clear previous dealer results when district changes
  };

  const handleStateSearch = (searchTerm: string) => {
    setStateSearchTerm(searchTerm);
    setShowStateDropdown(true);

    // If search term is cleared, reset everything
    if (!searchTerm.trim()) {
      setSelectedState('');
      setSelectedDistrict('');
      setDistrictSearchTerm('');
      setDealerNameSearchTerm('');
      setHasSearched(false);
      setLastSearchLabels(null);
      setDealers([]);
      setDistricts([]);
      setFilteredDistricts([]);
    }
  };

  const handleDistrictSearch = (searchTerm: string) => {
    setDistrictSearchTerm(searchTerm);
    setShowDistrictDropdown(true);

    // If district search is cleared, reset district and dealer results
    if (!searchTerm.trim()) {
      setSelectedDistrict('');
      setDealerNameSearchTerm('');
      setHasSearched(false);
      setLastSearchLabels(null);
      setDealers([]);
    }
  };

  const handleSearch = async () => {
    if (!selectedState || !selectedDistrict) {
      toast.error('Please select both state and district to search for dealers.');
      return;
    }

    const selectedStateObj = states.find((s) => s.id === selectedState);
    const selectedDistrictObj = districts.find((d) => d.id === selectedDistrict);
    const stateName = selectedStateObj?.name || stateSearchTerm || '';
    const districtName = selectedDistrictObj?.name || districtSearchTerm || '';

    // Execute reCAPTCHA before proceeding with search (best-effort).
    // A missing/failed token must not block dealer search — verify is soft
    // on the API side, and misconfigured keys would otherwise lock the tool.
    let recaptchaToken: string | null = null;
    if (isRecaptchaLoaded) {
      try {
        recaptchaToken = await executeRecaptcha('dealer_search');
      } catch (error) {
        console.warn('reCAPTCHA execute failed; continuing dealer search', error);
      }
    }

    // Mark search as attempted immediately so we never fall back to the
    // pre-search prompt after the request finishes (empty or error).
    setIsSearching(true);
    setHasSearched(true);
    setLastSearchLabels({ stateName, districtName });

    try {
      const searchResults = await searchDealers({
        stateId: String(selectedState),
        districtId: String(selectedDistrict),
        clienttypeId: '1',
        recaptchaToken: recaptchaToken || undefined,
      });

      const results = Array.isArray(searchResults) ? searchResults : [];
      const matchingResults = filterDealersByName(results, dealerNameSearchTerm);

      setDealers(results);

      // Save to Zustand store
      setDealersList(results);
      setSearchParams({
        selectedState,
        selectedDistrict,
        stateName,
        districtName,
      });

      if (results.length === 0) {
        toast.info('No dealers found in the selected location. Try searching in nearby areas.');
      } else if (matchingResults.length === 0) {
        toast.info(`No dealers match “${dealerNameSearchTerm.trim()}” in the selected location.`);
      } else {
        toast.success(
          `Found ${matchingResults.length} dealer(s) in ${districtName || selectedDistrict}, ${stateName || selectedState}`,
        );
        showSalesCallbackButton();
      }
    } catch (error) {
      console.error('Error searching dealers:', error);
      setDealers([]);
      toast.error('Failed to search dealers. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };

  const handleClearForm = () => {
    setSelectedState('');
    setSelectedDistrict('');
    setStateSearchTerm('');
    setDistrictSearchTerm('');
    setDealerNameSearchTerm('');
    setDealers([]);
    setDistricts([]);
    setFilteredStates(states);
    setFilteredDistricts([]);
    setShowStateDropdown(false);
    setShowDistrictDropdown(false);
    setIsSearching(false);
    setHasSearched(false);
    setLastSearchLabels(null);
    toast.info('Search form cleared');
  };

  const heroContent = getHeroContent();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="w-full overflow-hidden bg-black text-white">
        <div className="lg:hidden">
          <div className="relative aspect-[16/11] w-full bg-black">
            <CmsImage
              src={getBackgroundImage()}
              alt="Find a dealer"
              fill
              priority
              sizes="100vw"
              className="object-cover object-center"
              fallbackSrc={MEDIA_PLACEHOLDER}
            />
          </div>

          <div className="bg-black">
            <div className="container mx-auto px-4 py-8 sm:px-6 sm:py-10">
              <div className="max-w-2xl">
                <CmsRichTextHeading
                  className="hero-title text-white"
                  html={heroContent.titleRichText}
                  fallback={heroContent.title}
                />
                <p className="hero-subtitle mt-4 text-white/80">{heroContent.subtitle}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="relative hidden h-[700px] w-full lg:block">
          <div className="absolute inset-y-0 left-0 right-0 z-0 pointer-events-none lg:right-[620px]">
            <NextImage
              src="/images/dl_herodes.svg"
              alt=""
              fill
              aria-hidden="true"
              className="object-cover object-left-top opacity-90"
            />
          </div>

          <div className="container mx-auto relative z-10 h-full px-4 sm:px-6 lg:px-0">
            <div className="flex h-full max-w-md flex-col justify-center">
              <CmsRichTextHeading
                className="hero-title text-white"
                html={heroContent.titleRichText}
                fallback={heroContent.title}
              />
              <p className="hero-subtitle mt-5 text-white/80">{heroContent.subtitle}</p>
            </div>
          </div>
          <div className="absolute right-0 top-0 z-0 h-full w-[620px]">
            <CmsImage
              src={getBackgroundImage()}
              alt="Find a dealer"
              fill
              priority
              sizes="620px"
              className="object-cover object-center"
              fallbackSrc={MEDIA_PLACEHOLDER}
            />
          </div>
        </div>
      </section>

      {renderSpacersByType('common.spacer-top')}

      {/* Search and Results Section */}
      <div ref={searchSectionRef} className="container mx-auto scroll-mt-24 px-4 sm:px-6 lg:px-0">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:items-start">
          {/* Left: Search Form and Results */}
          <div className="space-y-6">
            <Card className="border border-gray-200 bg-white shadow-sm">
              <CardContent className="p-6 md:p-8">
                <h2 className="text-lg font-bold text-gray-900 md:text-xl">
                  {dealerFormComponent?.formHeading || 'Search for a Dealer'}
                </h2>
                {dealerFormComponent?.formSubheading && (
                  <p className="mt-2 text-sm text-gray-600">{dealerFormComponent.formSubheading}</p>
                )}

                {/* Geo-detect UI. Four mutually-exclusive UI states:
                    1. Working (isWorking)        → spinner with copy
                    2. Successfully detected      → "Showing dealers near X" + Change
                    3. Initial / denied / error   → prominent "Use my location" CTA
                       The CTA is always available so the user has a guaranteed
                       user-gesture path to trigger the browser permission popup.
                       Auto-prompt on mount is best-effort — modern browsers
                       (Chrome, Brave, Safari) block silent prompts in many
                       contexts. The button is the reliable fallback. */}
                <GeoDetectBanner
                  geo={geo}
                  onChangeLocation={() => {
                    clearGeoCache();
                    geoAppliedRef.current = false;
                    setSelectedState('');
                    setSelectedDistrict('');
                    setStateSearchTerm('');
                    setDistrictSearchTerm('');
                    setDealers([]);
                    setHasSearched(false);
                    setLastSearchLabels(null);
                  }}
                />

                <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="space-y-2 relative dropdown-container">
                    <Label htmlFor="dealer-state" className="text-sm text-gray-700">
                      {dealerFormComponent?.stateDistrictDropdown?.stateFieldLabel || 'State'}
                    </Label>
                    <div className="relative">
                      <Input
                        id="dealer-state"
                        type="text"
                        value={stateSearchTerm}
                        onChange={(e) => handleStateSearch(e.target.value)}
                        onFocus={() => setShowStateDropdown(true)}
                        placeholder={
                          isLoadingStates ? 'Loading states...' : 'Search and select state...'
                        }
                        disabled={isLoadingStates}
                        className="h-11 rounded-none border-0 border-b border-gray-200 bg-white pr-9 shadow-none focus-visible:border-0 focus-visible:border-b focus-visible:border-gray-200 focus-visible:ring-0 focus-visible:ring-offset-0 max-sm:!px-0"
                      />
                      {(stateSearchTerm || selectedState) && (
                        <button
                          type="button"
                          aria-label="Clear state and district"
                          className="absolute right-0 top-1/2 -translate-y-1/2 p-1.5 text-[#ed1c22] hover:opacity-80"
                          onClick={() => {
                            handleStateSearch('');
                            setShowStateDropdown(false);
                            setShowDistrictDropdown(false);
                          }}
                        >
                          <X className="h-4 w-4" strokeWidth={2.5} />
                        </button>
                      )}
                      {showStateDropdown && filteredStates.length > 0 && (
                        <div className="absolute top-full left-0 right-0 z-50 mt-1 max-h-60 overflow-y-auto bg-white border border-gray-200 rounded-md shadow-lg">
                          {filteredStates.map((state) => (
                            <div
                              key={state.id}
                              className="px-3 py-2 text-sm cursor-pointer hover:bg-gray-100"
                              onClick={() => handleStateChange(state.id)}
                            >
                              {state.name}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2 relative dropdown-container">
                    <Label htmlFor="dealer-district" className="text-sm text-gray-700">
                      {dealerFormComponent?.stateDistrictDropdown?.districtFieldLabel || 'District'}
                    </Label>
                    <div className="relative">
                      <Input
                        id="dealer-district"
                        type="text"
                        value={districtSearchTerm}
                        onChange={(e) => handleDistrictSearch(e.target.value)}
                        onFocus={() => setShowDistrictDropdown(true)}
                        placeholder={(() => {
                          if (isLoadingDistricts) return 'Loading districts...';
                          if (!selectedState) return 'Select state first';
                          return 'Search and select district...';
                        })()}
                        disabled={isLoadingDistricts || !selectedState}
                        className="h-11 rounded-none border-0 border-b border-gray-200 bg-white pr-9 shadow-none focus-visible:border-0 focus-visible:border-b focus-visible:border-gray-200 focus-visible:ring-0 focus-visible:ring-offset-0 max-sm:!px-0"
                      />
                      {(districtSearchTerm || selectedDistrict) && selectedState && (
                        <button
                          type="button"
                          aria-label="Clear district"
                          className="absolute right-0 top-1/2 -translate-y-1/2 p-1.5 text-[#ed1c22] hover:opacity-80"
                          onClick={() => {
                            handleDistrictSearch('');
                            setShowDistrictDropdown(false);
                          }}
                        >
                          <X className="h-4 w-4" strokeWidth={2.5} />
                        </button>
                      )}
                      {showDistrictDropdown && filteredDistricts.length > 0 && selectedState && (
                        <div className="absolute top-full left-0 right-0 z-50 mt-1 max-h-60 overflow-y-auto bg-white border border-gray-200 rounded-md shadow-lg">
                          {filteredDistricts.map((district) => (
                            <div
                              key={district.id}
                              className="px-3 py-2 text-sm cursor-pointer hover:bg-gray-100"
                              onClick={() => handleDistrictChange(district.id)}
                            >
                              {district.name}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mb-8 mt-6 flex flex-col gap-3 sm:flex-row">
                  <Button
                    onClick={handleSearch}
                    disabled={!selectedState || !selectedDistrict || isSearching}
                    className="h-12 w-full rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 sm:flex-1"
                  >
                    {isSearching
                      ? 'Searching...'
                      : dealerFormComponent?.submitButton?.[0]?.buttonText || 'Find Dealers'}
                  </Button>

                  <Button
                    onClick={handleClearForm}
                    variant="outline"
                    disabled={isSearching}
                    className="h-12 w-full rounded-md border-gray-300 px-6 text-gray-700 hover:bg-gray-50 sm:w-auto"
                  >
                    Clear
                  </Button>
                </div>

                {/* Dealer Results List */}
                {dealers.length > 0 && (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="dealer-name" className="text-sm text-gray-700">
                        Search dealers by name
                      </Label>
                      <div className="relative">
                        <Input
                          id="dealer-name"
                          type="text"
                          value={dealerNameSearchTerm}
                          onChange={(event) => {
                            setVisibleDealerCount(DEALER_RESULTS_BATCH_SIZE);
                            setDealerNameSearchTerm(event.target.value);
                          }}
                          placeholder="Search within these results..."
                          autoComplete="off"
                          className="h-11 rounded-none border-0 border-b border-gray-200 bg-white pr-9 shadow-none focus-visible:border-0 focus-visible:border-b focus-visible:border-gray-200 focus-visible:ring-0 focus-visible:ring-offset-0 max-sm:!px-0"
                        />
                        {dealerNameSearchTerm && (
                          <button
                            type="button"
                            aria-label="Clear dealer name"
                            className="absolute right-0 top-1/2 -translate-y-1/2 p-1.5 text-[#ed1c22] hover:opacity-80"
                            onClick={() => setDealerNameSearchTerm('')}
                          >
                            <X className="h-4 w-4" strokeWidth={2.5} />
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center justify-between gap-3">
                      <p className="text-[11px] font-bold uppercase tracking-wide text-gray-900">
                        NEAREST DEALERS
                      </p>
                      <p className="text-xs text-gray-500">
                        {dealerNameSearchTerm.trim()
                          ? `${filteredDealers.length} of ${dealers.length} Results`
                          : `${dealers.length} Results`}
                      </p>
                    </div>

                    {filteredDealers.length > 0 ? (
                      <div
                        ref={dealerResultsScrollRef}
                        className="max-h-[min(420px,55vh)] space-y-3 overflow-y-auto pr-1"
                      >
                        {filteredDealers.slice(0, visibleDealerCount).map((dealer) => (
                          <div
                            key={dealer.id}
                            className="rounded-lg bg-[#f8f7f4] p-4 cursor-pointer hover:bg-[#f0efe8] transition-colors"
                            onClick={() => {
                              const stateName =
                                states.find((s) => s.id === selectedState)?.name || 'Unknown';
                              const cityName = dealer.city || 'Unknown';
                              const dealerSlug = createDealerSlug(
                                dealer.dealerName || dealer.name || 'dealer',
                              );

                              // Save dealer data to Zustand store
                              setSelectedDealer(dealer);

                              const detailUrl = `/${locale}/tools/dealer-locator/${encodeURIComponent(stateName)}/${encodeURIComponent(cityName)}/${dealerSlug}?id=${dealer.id}`;
                              router.push(detailUrl);
                            }}
                          >
                            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
                              <h3 className="text-sm font-bold leading-snug text-gray-900 md:text-base">
                                {dealer.dealerName || dealer.name}
                              </h3>
                              <div className="flex shrink-0 items-center gap-1 text-xs font-medium text-gray-900">
                                <MapPin
                                  className="h-3.5 w-3.5 shrink-0 text-gray-900"
                                  aria-hidden
                                />
                                <span>{dealer.city ?? 'Ahmedabad'}</span>
                              </div>
                            </div>
                            <p className="mt-2 text-xs leading-relaxed text-gray-600">
                              {dealer.address}
                            </p>
                            <div className="mt-3 flex flex-col gap-2 border-t border-gray-100 pt-3 sm:flex-row sm:items-stretch sm:gap-3">
                              <a
                                href={`tel:${(dealer.phone || '').replace(/\s/g, '')}`}
                                className="flex items-center justify-center gap-1.5 rounded-md bg-white py-2 text-xs font-medium text-gray-900 sm:flex-1"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <span className="inline-flex size-5 shrink-0 items-center justify-center">
                                  <Phone
                                    className="size-4 text-green-600"
                                    strokeWidth={2.5}
                                    aria-hidden
                                  />
                                </span>
                                <span className="truncate">{dealer.phone || 'N/A'}</span>
                              </a>
                              <a
                                href={`tel:${((dealer.phone2 ?? dealer.phone) || '').replace(/\s/g, '')}`}
                                className="flex items-center justify-center gap-1.5 rounded-md bg-white py-2 text-xs font-medium text-gray-900 sm:flex-1"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <span className="inline-flex size-5 shrink-0 items-center justify-center">
                                  <Phone
                                    className="size-4 text-green-600"
                                    strokeWidth={2.5}
                                    aria-hidden
                                  />
                                </span>
                                <span className="truncate">
                                  {dealer.phone2 ?? dealer.phone ?? 'N/A'}
                                </span>
                              </a>
                              <div className="flex shrink-0 items-center">
                                <Button
                                  type="button"
                                  size="icon"
                                  className="h-10 w-full rounded-md border border-blue-600 bg-transparent p-0 text-white hover:bg-white sm:h-9 sm:w-9"
                                  aria-label="Directions"
                                  onClick={(e) => {
                                    e.stopPropagation();

                                    // Open Google Maps with dealer location
                                    if (
                                      dealer.latitude &&
                                      dealer.longitude &&
                                      dealer.latitude !== 0 &&
                                      dealer.longitude !== 0
                                    ) {
                                      const mapsUrl = `https://maps.google.com/maps?q=${dealer.latitude},${dealer.longitude}&hl=en`;
                                      window.open(mapsUrl, '_blank');
                                    } else {
                                      const searchQuery = encodeURIComponent(
                                        `${dealer.dealerName || dealer.name} ${dealer.address} ${dealer.city} ${dealer.state}`,
                                      );
                                      const mapsUrl = `https://maps.google.com/maps?q=${searchQuery}&hl=en`;
                                      window.open(mapsUrl, '_blank');
                                    }
                                  }}
                                >
                                  <NextImage
                                    src="/images/directions.svg"
                                    alt=""
                                    width={20}
                                    height={20}
                                    className="h-5 w-5"
                                    aria-hidden
                                  />
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                        {visibleDealerCount < filteredDealers.length && (
                          <div
                            ref={loadMoreDealersRef}
                            className="flex min-h-8 items-center justify-center py-2 text-xs text-gray-500"
                            aria-live="polite"
                          >
                            Showing {visibleDealerCount} of {filteredDealers.length} dealers
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="rounded-lg bg-[#f8f7f4] px-4 py-8 text-center">
                        <p className="text-sm font-semibold text-gray-900">
                          No dealers match “{dealerNameSearchTerm.trim()}”
                        </p>
                        <p className="mt-1 text-xs text-gray-600">
                          Try another dealer name or clear the optional search field.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Default state when no search performed */}
            {!hasSearched && !lastSearchLabels && dealers.length === 0 && !isSearching && (
              <div className="flex min-h-[310px] flex-col items-center justify-center px-4 py-8 text-center">
                <NextImage
                  src="/images/locater.svg"
                  alt="Location placeholder"
                  width={172}
                  height={120}
                  className="h-auto w-[172px]"
                />
                <p className="mt-8 max-w-[340px] text-lg font-bold leading-tight text-[#111827] md:text-[24px]">
                  Please enter a location to find Wonder Cement dealers near you.
                </p>
              </div>
            )}

            {/* No results state — only after Search has run */}
            {(hasSearched || lastSearchLabels) && dealers.length === 0 && !isSearching && (
              <div className="flex min-h-[310px] flex-col items-center justify-center px-4 py-8 text-center">
                <NextImage
                  src="/images/locater.svg"
                  alt="No results"
                  width={172}
                  height={120}
                  className="h-auto w-[172px]"
                />
                <p className="mt-8 max-w-[340px] text-lg font-bold leading-tight text-[#111827] md:text-[24px]">
                  No dealers found in{' '}
                  {lastSearchLabels?.districtName ||
                    districts.find((d) => d.id === selectedDistrict)?.name ||
                    districtSearchTerm ||
                    'this district'}
                  ,{' '}
                  {lastSearchLabels?.stateName ||
                    states.find((s) => s.id === selectedState)?.name ||
                    stateSearchTerm ||
                    'this state'}
                  .
                </p>
                <p className="mt-2 text-sm text-gray-600">
                  Try searching in nearby areas or contact us for assistance.
                </p>
              </div>
            )}
          </div>

          {/* Right: Map. Lazy-mounted — see `showMap` state above. Renders
              a static JPG placeholder until the user either runs a search
              (dealers populated) or scrolls the container near the viewport.
              Both states use the same fixed container dimensions so the
              swap-in does not cause any layout shift (CLS-safe). */}
          <div
            ref={mapContainerRef}
            className="relative min-h-[220px] w-full overflow-hidden rounded-xl border border-gray-200 bg-gray-100 shadow-sm sm:min-h-[320px] lg:min-h-[min(640px,calc(100vh-8rem))] lg:sticky lg:top-24"
          >
            <div className="relative h-[280px] w-full sm:h-[400px] lg:h-[637px]">
              {showMap ? (
                <GoogleMap
                  dealers={filteredDealers}
                  center={geoMapCenter}
                  zoom={geoMapCenter ? 11 : 10}
                  className="h-full w-full"
                />
              ) : (
                <CmsImage
                  src="/images/map_img.jpg"
                  alt="Dealer locations map"
                  fill
                  sizes="(max-width: 1024px) 100vw, 50vw"
                  className="object-cover"
                  priority={false}
                  fallbackSrc={MEDIA_PLACEHOLDER}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {renderSpacersByType('common.spacer-full')}

      {/* Media Slider Section */}
      {mediaSliderComponent && (
        <section>
          <div className="container mx-auto px-4 sm:px-6 lg:px-0">
            <div className="mb-8 flex flex-col gap-4 md:mb-10 md:flex-row md:items-start md:justify-between">
              <h2 className="max-w-2xl text-3xl font-bold leading-tight text-[#0f172a] sm:text-4xl md:text-5xl">
                {mediaSliderComponent.heading}
              </h2>
              <div className="flex flex-col items-start gap-3 md:items-end">
                {mediaSliderComponent.button?.[0] && (
                  <SwapTextButton
                    text={mediaSliderComponent.button[0].buttonText}
                    hoverText={mediaSliderComponent.button[0].buttonText}
                    className="w-full justify-center px-6 py-3 text-sm font-semibold !bg-red-600 !text-white hover:!bg-transparent hover:!text-red-600 hover:!border-red-600 sm:w-auto"
                    variant="outline"
                    href={
                      isPlaceholderNavHref(mediaSliderComponent.button[0].buttonUrl)
                        ? `/${locale}/contact`
                        : (localizeUrl(mediaSliderComponent.button[0].buttonUrl, locale) ??
                          `/${locale}/contact`)
                    }
                    {...getCmsButtonLinkProps(mediaSliderComponent.button[0])}
                  />
                )}
              </div>
            </div>

            <Carousel
              opts={{
                align: 'start',
                loop: true,
                watchDrag: false,
              }}
              className="w-full"
            >
              <CarouselContent className="-ml-4">
                {mediaSliderComponent.slide.map((slide) => (
                  <CarouselItem
                    key={slide.id}
                    className="basis-[88%] pl-4 sm:basis-1/2 lg:basis-1/3"
                  >
                    <div className="relative aspect-[16/10] overflow-hidden rounded-2xl bg-black">
                      <DealerSlideMedia
                        slide={slide}
                        isPlaying={playingSlideId === slide.id}
                        onPlay={() => setPlayingSlideId(slide.id)}
                      />
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious
                className="left-3 sm:left-4"
                onClick={() => setPlayingSlideId(null)}
              />
              <CarouselNext
                className="right-3 sm:right-4"
                onClick={() => setPlayingSlideId(null)}
              />
            </Carousel>
          </div>
        </section>
      )}

      {renderSpacersByType('common.spacer-bottom')}

      {/* FAQ Section */}
      {faqComponent && (
        <FaqEnquirySection
          faqComponent={{
            __component: faqComponent.__component,
            id: faqComponent.id,
            heading: faqComponent.heading,
            subheading: faqComponent.subheading,
            faqs: faqComponent.faqs,
            lead_forms: faqComponent.lead_forms,
          }}
          leadForms={leadForms}
        />
      )}
    </div>
  );
}
